import { Children, cloneElement, isValidElement } from "react";
import { ImageRun } from "docx";
import { startCase } from "lodash";

import { DEFAULT_ICON_SIZE, IconProps, resolveIconToReactSvg } from "lib/icons/untitled_ui/UIcon";
import { applyColorOverrides, resolveColor } from "./utils";

type DocxIconProps = IconProps & {
  altText?: {
    name: string;
    description?: string;
    title?: string;
  };
};

// SVG attributes that are camelCase in both React JSX and the SVG spec — do not convert.
const SVG_CAMEL_CASE_ATTRS = new Set([
  "viewBox",
  "gradientTransform",
  "gradientUnits",
  "patternTransform",
  "patternUnits",
  "patternContentUnits",
  "clipPathUnits",
  "markerWidth",
  "markerHeight",
  "markerUnits",
  "spreadMethod",
  "tableValues",
  "startOffset",
  "refX",
  "refY",
]);

function toSvgAttrName(key: string): string {
  if (SVG_CAMEL_CASE_ATTRS.has(key)) return key;
  return key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}

function serializeSvg(node: React.ReactNode): string {
  if (node === null || node === undefined) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (!isValidElement(node)) return "";

  const { type, props } = node as React.ReactElement;
  if (typeof type !== "string") return "";

  const { children, ...attrs } = props as Record<string, unknown>;

  const attrString = Object.entries(attrs)
    .filter(([, v]) => v !== undefined && v !== null && v !== false)
    .map(([k, v]) => `${toSvgAttrName(k)}="${v}"`)
    .join(" ");

  const childString = Children.toArray(children as React.ReactNode)
    .map(serializeSvg)
    .join("");

  const openTag = attrString ? `<${type} ${attrString}` : `<${type}`;
  return childString ? `${openTag}>${childString}</${type}>` : `${openTag}/>`;
}

/**
 * Resolves currentColor/inherit in the element tree so the serialized SVG string
 * contains explicit colour values — browsers handle this via CSS cascade but a
 * static SVG string does not.
 */
function flattenSvgColors(
  node: React.ReactNode,
  resolvedColor: string,
  inheritedFill?: string,
): React.ReactNode {
  if (!isValidElement(node)) return node;

  const props: Record<string, unknown> = node.props;
  const overrides: Record<string, unknown> = applyColorOverrides(
    props,
    resolvedColor,
    inheritedFill,
  );

  const nextInheritedFill =
    "fill" in overrides && typeof overrides.fill === "string"
      ? overrides.fill
      : "fill" in props && typeof props.fill === "string"
        ? props.fill
        : inheritedFill;

  const children = props.children as React.ReactNode | undefined;
  if (children) {
    overrides.children = Children.toArray(children).map((child) =>
      flattenSvgColors(child, resolvedColor, nextInheritedFill),
    );
  }

  return Object.keys(overrides).length > 0 ? cloneElement(node, overrides) : node;
}

async function svgToPng(svgString: string, size: number): Promise<Buffer> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(new Blob([svgString], { type: "image/svg+xml" }));

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error("Failed to get canvas 2d context"));
        return;
      }
      ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Failed to export canvas as PNG"));
          return;
        }
        blob
          .arrayBuffer()
          .then((buf) => resolve(Buffer.from(buf)))
          .catch(reject);
      }, "image/png");
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load SVG for PNG conversion"));
    };
    img.src = url;
  });
}

export const DocxUIcon = async ({
  icon,
  size,
  color,
  altText,
}: DocxIconProps): Promise<ImageRun> => {
  const svg = resolveIconToReactSvg(icon);
  if (!svg || !isValidElement(svg)) {
    throw new Error(`Icon ${icon} not found or isn't valid when rendering for pdf export`);
  }
  const resolvedColor = resolveColor(color, "sysPrimaryDefault");
  const resolvedSize = size ?? DEFAULT_ICON_SIZE;

  const resolved = flattenSvgColors(svg, resolvedColor) as React.ReactElement;
  const sizedSvg = cloneElement(resolved, { width: resolvedSize, height: resolvedSize });
  const svgString = serializeSvg(sizedSvg);
  const pngData = await svgToPng(svgString, resolvedSize);

  return new ImageRun({
    type: "svg",
    data: Buffer.from(svgString),
    transformation: {
      width: resolvedSize,
      height: resolvedSize,
    },
    altText: {
      name: altText?.name ?? `${startCase(icon)} icon`,
      ...altText,
    },
    fallback: {
      // Not all docx renderers support SVGs (e.g. Apple Pages) so we need a fallback
      type: "png",
      data: pngData,
    },
  });
};
